import { useEffect, useState } from 'react'
import { Zap, TrendingUp, Users, CheckCircle2, AlertTriangle, Share2, Users2, ArrowRight, Sparkles, Flame, Medal, Trophy, Award } from 'lucide-react'
import { Link } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { api, AccountabilityPair } from '../lib/api'
import { useAuthStore } from '../store/authStore'
import { UpgradeModal, useUpgradeGuard } from '../components/UpgradeModal'
import clsx from 'clsx'

const INK = '#1a3a40'
const BRAND_TEAL = '#2A7A8A'
const BRAND_SAGE = '#B8C7B1'
const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'

interface Summary {
  engagementScore: { score: number; computed_at: string } | null
  riskFlags: string[]
  frictionScore: number
  weeklyStats: { sent: number; opened: number; acted: number } | null
  activeMemberCount: number
  isAnonymized: boolean
}

interface MabStat {
  category: string
  success_count: number
  total_count: number
  conversion_rate: number
}

interface EngagementPoint {
  computed_at: string
  score: number
}

interface PersonalNudgeProgress {
  streakDays: number
  completedThisWeek: number
  completedThisMonth: number
  snoozedThisWeek: number
  sentThisWeek: number
}

export function HomePage() {
  const { user } = useAuthStore()
  const { upgradeError, setUpgradeError, withUpgradeGuard } = useUpgradeGuard()

  const [summary, setSummary] = useState<Summary | null>(null)
  const [trend, setTrend] = useState<EngagementPoint[]>([])
  const [mabStats, setMabStats] = useState<MabStat[]>([])
  const [loading, setLoading] = useState(true)
  const [sharing, setSharing] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [shareCopied, setShareCopied] = useState(false)

  const [myPair, setMyPair] = useState<AccountabilityPair | null>(null)
  const [buddyLoading, setBuddyLoading] = useState(true)

  const [goal, setGoal] = useState('')
  const [track, setTrack] = useState<string | null>(null)
  const [settingGoal, setSettingGoal] = useState(false)

  const [myProgress, setMyProgress] = useState<PersonalNudgeProgress | null>(null)
  const [progressLoading, setProgressLoading] = useState(true)

  const [insight, setInsight] = useState<{ insight: string; generatedAt: string } | null>(null)
  const [insightLoading, setInsightLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    // Load independently: if engagement or MAB fails, dashboard summary must still populate.
    api
      .getSummary()
      .then(s => {
        if (!cancelled) setSummary(s as Summary)
      })
      .catch(() => {
        if (!cancelled) setSummary(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    api
      .getEngagement(30)
      .then(e => {
        if (!cancelled) setTrend((e as { scores: EngagementPoint[] }).scores ?? [])
      })
      .catch(() => {
        if (!cancelled) setTrend([])
      })

    api
      .getMabStats()
      .then(m => {
        if (!cancelled) setMabStats((m as { stats: MabStat[] }).stats ?? [])
      })
      .catch(() => {
        if (!cancelled) setMabStats([])
      })

    api.getAccountabilityPairs()
      .then(res => {
        const pairs = (res as { pairs: AccountabilityPair[] }).pairs ?? []
        const mine = pairs.find(p => p.user_a_id === user?.id || p.user_b_id === user?.id)
        setMyPair(mine ?? null)
      })
      .catch(() => { /* non-critical */ })
      .finally(() => setBuddyLoading(false))

    api.getGoal()
      .then(res => {
        if (!cancelled && res) {
          setGoal(res.goal)
          setTrack(res.track)
        }
      })
      .catch(() => { /* non-critical */ })

    setProgressLoading(true)
    api
      .getNudgeProgress()
      .then(p => {
        if (!cancelled) setMyProgress(p)
      })
      .catch(() => {
        if (!cancelled) setMyProgress(null)
      })
      .finally(() => {
        if (!cancelled) setProgressLoading(false)
      })

    setInsightLoading(true)
    api.getInsights()
      .then(res => {
        if (!cancelled) setInsight(res)
      })
      .catch(() => {
        if (!cancelled) setInsight(null)
      })
      .finally(() => {
        if (!cancelled) setInsightLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [user?.id])

  async function handleShare() {
    setSharing(true)
    setShareCopied(false)
    try {
      const res = await withUpgradeGuard(() => api.generateReport())
      if (res) setShareUrl((res as { shareUrl: string }).shareUrl)
    } finally {
      setSharing(false)
    }
  }

  async function copyShareLink() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2500)
    } catch {
      /* ignore */
    }
  }

  async function handleSetGoal(e: React.FormEvent) {
    e.preventDefault()
    if (!goal.trim()) return
    setSettingGoal(true)
    try {
      const res = await api.postGoal(goal)
      setTrack(res.track)
    } finally {
      setSettingGoal(false)
    }
  }

  const riskFlags = summary?.riskFlags ?? []

  const score = summary?.engagementScore?.score ?? null
  const scoreColor =
    score === null
      ? 'text-ink-muted'
      : score >= 70
        ? 'text-emerald-800'
        : score >= 40
          ? 'text-amber-800'
          : 'text-red-700'

  const chartData = trend.map(p => ({
    date: new Date(p.computed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: p.score,
  }))

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {upgradeError && (
        <UpgradeModal error={upgradeError} onClose={() => setUpgradeError(null)} />
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            Good {getTimeOfDay()}, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>Here&apos;s how your team is doing</p>
        </div>
        <button
          onClick={handleShare}
          disabled={sharing}
          className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal"
          style={{ background: '#2A7A8A' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#236b7a')}
          onMouseLeave={e => (e.currentTarget.style.background = '#2A7A8A')}
        >
          <Share2 size={15} />
          {sharing ? 'Generating…' : 'Share Report'}
        </button>
      </div>

      {shareUrl && (
        <div
          className="mb-6 rounded-xl border px-4 py-4 space-y-3"
          style={{ background: WARM_CARD, borderColor: BRAND_TEAL, borderWidth: 1, boxShadow: '0 0 24px -8px rgba(42,122,138,0.25)' }}
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 size={18} className="text-brand-teal shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">Shareable report is ready</p>
              <p className="text-xs text-ink-muted mt-1 leading-relaxed">
                Opens a formatted HTML summary: engagement score, 30-day and weekly nudge activity, trend, risk signals, top nudges by action rate, and track effectiveness (MAB). Anyone with the link can view it—no login required.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 pl-[30px]">
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-white px-4 py-2 rounded-lg transition-colors shadow-glow-teal"
              style={{ background: BRAND_TEAL }}
            >
              Open report
              <ArrowRight size={14} />
            </a>
            <button
              type="button"
              onClick={() => void copyShareLink()}
              className="text-sm font-medium px-4 py-2 rounded-lg border border-warm-border bg-warm-card hover:bg-warm-inset text-ink transition-colors"
            >
              {shareCopied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
          <p className="text-[11px] text-ink-faint font-mono break-all pl-[30px]">{shareUrl}</p>
        </div>
      )}

      {/* AI Executive Insight Card */}
      <div 
        className="mb-8 rounded-2xl border p-6 relative overflow-hidden shadow-card animate-fade-in"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="absolute top-0 right-0 p-3 opacity-10 pointer-events-none">
          <Sparkles size={80} className="text-brand-teal" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-brand-teal/10 p-1.5 rounded-lg">
              <Sparkles size={16} className="text-brand-teal" />
            </div>
            <h2 className="text-sm font-semibold text-ink uppercase tracking-wider">Executive Plan Insight</h2>
            <span className="ml-auto text-[10px] font-bold text-brand-teal bg-brand-teal/5 px-2 py-0.5 rounded-md border border-brand-teal/20">
              DUVA AI
            </span>
          </div>
          
          {insightLoading ? (
            <div className="space-y-2">
              <div className="h-4 bg-warm-inset rounded animate-pulse w-full" />
              <div className="h-4 bg-warm-inset rounded animate-pulse w-5/6" />
            </div>
          ) : insight ? (
            <p className="text-base text-ink leading-relaxed font-medium">
              “{insight.insight}”
            </p>
          ) : (
            <p className="text-sm text-ink-soft">
              AI analysis is pending more team activity. Check back soon for your weekly executive summary.
            </p>
          )}
          
          {insight?.generatedAt && (
            <p className="mt-4 text-[10px] text-ink-faint">
              Analysis based on metrics from the last 30 days · Updated {new Date(insight.generatedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <GlowMetricCard
          label="Engagement Score"
          glow="teal"
          breathe
          scoreProgress={score}
          icon={<TrendingUp size={18} className="text-brand-teal" />}
          loading={loading}
          tooltip="A weighted index of team interaction with nudges over the last 30 days. Higher = more active habit building."
        >
          <span className={clsx('text-2xl font-bold tabular-nums', scoreColor)}>
            {score !== null ? score : '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">/100</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Nudges Sent"
          glow="sage"
          breathe
          icon={<Zap size={18} style={{ color: BRAND_SAGE }} />}
          loading={loading}
        >
          <span className="text-2xl font-bold text-ink tabular-nums">
            {summary?.weeklyStats?.sent ?? '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">this week</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Actions Taken"
          glow="teal"
          icon={<CheckCircle2 size={18} className="text-brand-teal" />}
          loading={loading}
        >
          <span className="text-2xl font-bold text-ink tabular-nums">
            {summary?.weeklyStats?.acted ?? '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">this week</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Behavioral ROI (Friction)"
          glow="teal"
          icon={<Zap size={18} className="text-brand-teal" />}
          loading={loading}
          tooltip="The percentage of nudges snoozed or skipped. Low friction indicates nudges are easy to act on."
        >
          <span className={clsx('text-2xl font-bold tabular-nums', (summary?.frictionScore ?? 0) > 30 ? 'text-red-700' : 'text-emerald-800')}>
            {summary?.frictionScore ?? 0}%
          </span>
          <span className="text-xs text-ink-soft ml-1">friction</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Active Members"
          glow="sage"
          icon={<Users size={18} style={{ color: BRAND_SAGE }} />}
          loading={loading}
        >
          <span className="text-2xl font-bold text-ink tabular-nums">
            {summary?.activeMemberCount ?? '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">receiving nudges</span>
        </GlowMetricCard>
      </div>

      <div
        className="mb-8 rounded-2xl border p-6 shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Flame size={18} className="text-amber-700 shrink-0" />
            <h2 className="text-sm font-semibold text-ink">Your nudge activity</h2>
          </div>
          <p className="text-xs text-ink-soft">Your choices · rolling 7 days</p>
        </div>
        {progressLoading ? (
          <div className="h-[4.5rem] rounded-xl animate-pulse" style={{ background: '#f0ece4' }} />
        ) : myProgress ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted mb-1">Streak</p>
              <p className="text-2xl font-bold text-ink tabular-nums">
                {myProgress.streakDays > 0 ? myProgress.streakDays : '—'}
              </p>
              <p className="text-xs text-ink-soft mt-1 leading-snug">
                {myProgress.streakDays > 0
                  ? `${myProgress.streakDays === 1 ? 'day' : 'days'} in a row with at least one “Done”`
                  : 'Tap Done on a nudge to start a streak'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted mb-1">Marked done</p>
              <p className="text-2xl font-bold text-ink tabular-nums">{myProgress.completedThisWeek}</p>
              <p className="text-xs text-ink-soft mt-1 leading-snug">This week</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">Badges & MVP</p>
                <span className="text-[10px] text-brand-teal font-bold">{myProgress.completedThisMonth} this month</span>
              </div>
              <div className="flex gap-2">
                <BadgeIcon 
                  icon={<Medal size={14} />} 
                  label="Silver" 
                  active={myProgress.completedThisMonth >= 4} 
                  color="text-slate-400"
                  tooltip="4+ actions/mo"
                />
                <BadgeIcon 
                  icon={<Award size={14} />} 
                  label="Gold" 
                  active={myProgress.completedThisMonth >= 8} 
                  color="text-amber-500"
                  tooltip="8+ actions/mo"
                />
                <BadgeIcon 
                  icon={<Trophy size={14} />} 
                  label="Diamond" 
                  active={myProgress.completedThisMonth >= 12} 
                  color="text-brand-teal"
                  tooltip="12+ actions/mo"
                />
              </div>
              <p className="text-[10px] text-ink-soft mt-2 leading-tight">
                {myProgress.completedThisMonth >= 12 
                  ? "Diamond MVP! You're a habit-building pro." 
                  : myProgress.completedThisMonth >= 8 
                    ? "Gold Nudger! 4 more to Diamond." 
                    : myProgress.completedThisMonth >= 4 
                      ? "Silver Nudger! 4 more to Gold." 
                      : `${4 - myProgress.completedThisMonth} more actions to reach Silver MVP.`}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink-muted">
            Couldn&apos;t load your personal stats. They appear after you use Done or Remind me later on a nudge.
          </p>
        )}
      </div>

      <div className="mb-8 rounded-2xl border p-6 shadow-card" style={{ background: WARM_CARD, borderColor: WARM_BORDER }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-brand-teal" />
          <h2 className="text-sm font-semibold text-ink">Personal AI Coach</h2>
        </div>
        <p className="text-sm text-ink-muted mb-4">
          What do you want to get better at this month? Our AI will personalize your nudges.
        </p>
        <form onSubmit={handleSetGoal} className="flex gap-3">
          <input
            type="text"
            value={goal}
            onChange={e => setGoal(e.target.value)}
            placeholder="e.g., I struggle to speak up in large meetings..."
            className="flex-1 rounded-xl px-4 py-2.5 text-sm text-ink outline-none transition-colors"
            style={{ background: '#f0ece4', border: `1px solid ${WARM_BORDER}` }}
          />
          <button
            type="submit"
            disabled={settingGoal || !goal.trim()}
            className="text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 whitespace-nowrap shadow-glow-teal"
            style={{ background: '#2A7A8A' }}
          >
            {settingGoal ? 'Thinking...' : 'Update tracking'}
          </button>
        </form>
        {track && (
          <div className="mt-4 flex items-center gap-2 text-sm text-brand-teal bg-[#EBF6FB] px-4 py-3 rounded-lg border" style={{ borderColor: '#a7d8e0' }}>
            <CheckCircle2 size={16} />
            <p>You are currently on the <strong className="capitalize">{track}</strong> track.</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div
          className="lg:col-span-2 rounded-2xl border p-6 shadow-card"
          style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
        >
          <h2 className="text-sm font-semibold text-ink mb-4">Engagement Score — Last 30 Days</h2>
          {summary?.isAnonymized ? (
            <div className="h-[200px] flex flex-col items-center justify-center text-center px-4">
              <Users size={32} className="text-ink-faint mb-3" />
              <p className="text-sm text-ink-muted font-medium">Anonymity Guard Active</p>
              <p className="text-xs text-ink-soft mt-1">Detailed trends are hidden for teams with fewer than 3 members to protect individual privacy.</p>
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: INK }}
                  tickLine={false}
                  axisLine={{ stroke: WARM_BORDER }}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: INK }}
                  tickLine={false}
                  axisLine={{ stroke: WARM_BORDER }}
                  width={32}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null
                    return (
                      <div
                        className="rounded-lg px-3 py-2 text-xs shadow-card border"
                        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
                      >
                        <p className="text-ink font-medium mb-0.5">{String(label)}</p>
                        <p className="text-brand-teal font-semibold tabular-nums">
                          {(payload[0].value as number)} / 100
                        </p>
                      </div>
                    )
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={BRAND_TEAL}
                  strokeWidth={3}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  dot={false}
                  activeDot={{ r: 5, fill: BRAND_TEAL, stroke: WARM_CARD, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="Engagement data will appear here once nudges start being delivered." />
          )}
        </div>

        {mabStats.length > 0 && !summary?.isAnonymized && (
          <div
            className="rounded-2xl border p-6 shadow-card"
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-ink">Winning Tracks</h2>
              <Sparkles size={16} className="text-brand-teal" />
            </div>
            <div className="space-y-4">
              {mabStats.slice(0, 3).map((stat, i) => (
                <div key={stat.category} className="relative">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="capitalize font-medium text-ink-muted">{stat.category}</span>
                    <span className="text-brand-teal font-bold">{stat.conversion_rate}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-warm-inset rounded-full overflow-hidden">
                    <div 
                      className={clsx("h-full rounded-full transition-all duration-1000", i === 0 ? "bg-brand-teal" : "bg-brand-sage")} 
                      style={{ width: `${stat.conversion_rate}%` }} 
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-[10px] text-ink-soft leading-tight">
              Calculated via Multi-Armed Bandit (MAB) selection based on your team&apos;s real-time interaction patterns.
            </p>
          </div>
        )}
        <BuddyWidget pair={myPair} userId={user?.id ?? ''} loading={buddyLoading} />
      </div>

      {riskFlags.length > 0 && (
        <div
          className="rounded-2xl border p-5"
          style={{ background: '#fdf8f0', borderColor: '#e8d4b8' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-700" />
            <h2 className="text-sm font-semibold text-ink">Attention needed</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {riskFlags.map(flag => (
              <span
                key={flag}
                className="text-xs px-3 py-1 rounded-full border text-amber-900"
                style={{ background: '#fff9f0', borderColor: '#e8d4b8' }}
              >
                {formatFlag(flag)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function GlowMetricCard({
  label,
  icon,
  children,
  loading,
  glow,
  breathe,
  scoreProgress,
  tooltip,
}: {
  label: string
  icon: React.ReactNode
  children: React.ReactNode
  loading?: boolean
  glow: 'teal' | 'sage'
  breathe?: boolean
  scoreProgress?: number | null
  tooltip?: string
}) {
  const glowClass = glow === 'teal' ? 'glow-radial-teal' : 'glow-radial-sage'
  const glowOpacity =
    glow === 'teal' && scoreProgress != null
      ? 0.55 + (Math.min(100, Math.max(0, scoreProgress)) / 100) * 0.45
      : 1

  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded-2xl border p-5 shadow-card',
        breathe && 'animate-breathe'
      )}
      style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
    >
      <div
        className={clsx('absolute inset-0 pointer-events-none', glowClass)}
        style={{ opacity: glowOpacity }}
        aria-hidden
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col">
            <span className="text-xs font-medium text-ink-soft">{label}</span>
            {tooltip && <span className="text-[10px] text-ink-faint leading-tight mt-0.5 max-w-[120px]">{tooltip}</span>}
          </div>
          {icon}
        </div>
        {loading ? (
          <div className="h-8 w-20 rounded animate-pulse bg-warm-inset" />
        ) : (
          <div className="flex items-baseline gap-1 flex-wrap">{children}</div>
        )}
      </div>
    </div>
  )
}

function BuddyWidget({ pair, userId, loading }: {
  pair: AccountabilityPair | null
  userId: string
  loading: boolean
}) {
  const buddyName = pair
    ? (pair.user_a_id === userId ? pair.user_b_name : pair.user_a_name)
    : null
  const buddyEmail = pair
    ? (pair.user_a_id === userId ? pair.user_b_email : pair.user_a_email)
    : null
  const initial = (buddyName ?? buddyEmail ?? '?')[0]?.toUpperCase()

  const trackLabel = pair?.nudge_track
    ? pair.nudge_track.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : null

  return (
    <div
      className="flex flex-col rounded-2xl border p-6 shadow-card"
      style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-ink">Accountability Buddy</h2>
        <Link to="/accountability" className="text-xs text-brand-teal hover:text-brand-teal-hover flex items-center gap-1 font-medium">
          View all <ArrowRight size={11} />
        </Link>
      </div>

      {loading ? (
        <div className="flex-1 flex flex-col gap-3 animate-pulse">
          <div className="h-12 w-12 rounded-full bg-warm-inset mx-auto" />
          <div className="h-4 bg-warm-inset rounded w-3/4 mx-auto" />
          <div className="h-3 bg-warm-inset rounded w-1/2 mx-auto" />
        </div>
      ) : pair ? (
        <div className="flex-1 flex flex-col items-center text-center relative">
          <div
            className="absolute inset-x-0 top-0 h-32 pointer-events-none glow-radial-teal opacity-50 rounded-full"
            aria-hidden
          />
          <div className="relative w-14 h-14 rounded-full bg-brand-teal flex items-center justify-center text-xl font-bold text-white mb-3 shadow-glow-teal">
            {initial}
          </div>
          <p className="text-sm font-semibold text-ink leading-tight">{buddyName ?? buddyEmail}</p>
          {buddyEmail && buddyName && (
            <p className="text-xs text-ink-soft mt-1 truncate max-w-full">{buddyEmail}</p>
          )}
          {trackLabel && (
            <span
              className="mt-3 text-xs px-2.5 py-0.5 rounded-full border font-medium"
              style={{ background: '#EBF6FB', color: BRAND_TEAL, borderColor: '#a7d8e0' }}
            >
              {trackLabel} track
            </span>
          )}
          {buddyEmail && (
            (() => {
              const buddySlackId = pair.user_a_id === userId ? pair.user_b_slack_id : pair.user_a_slack_id
              const buddyTeamsId = pair.user_a_id === userId ? pair.user_b_teams_id : pair.user_a_teams_id

              if (buddySlackId) {
                return (
                  <a
                    href={`slack://user?team=${import.meta.env.VITE_SLACK_TEAM_ID ?? ''}&id=${buddySlackId}`}
                    className="mt-4 w-full text-center text-xs font-medium px-3 py-2 rounded-lg transition-colors border text-brand-teal hover:bg-warm-inset"
                    style={{ borderColor: WARM_BORDER, background: WARM_CARD }}
                  >
                    Check in on Slack →
                  </a>
                )
              }

              if (buddyTeamsId) {
                return (
                  <a
                    href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(buddyEmail)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 w-full text-center text-xs font-medium px-3 py-2 rounded-lg transition-colors border text-brand-teal hover:bg-warm-inset"
                    style={{ borderColor: WARM_BORDER, background: WARM_CARD }}
                  >
                    Check in on Teams →
                  </a>
                )
              }

              return (
                <a
                  href={`mailto:${buddyEmail}?subject=Accountability%20check-in`}
                  className="mt-4 w-full text-center text-xs font-medium px-3 py-2 rounded-lg transition-colors border text-brand-teal hover:bg-warm-inset"
                  style={{ borderColor: WARM_BORDER, background: WARM_CARD }}
                >
                  Send a check-in →
                </a>
              )
            })()
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-warm-inset flex items-center justify-center border" style={{ borderColor: WARM_BORDER }}>
            <Users2 size={18} className="text-ink-soft" />
          </div>
          <p className="text-xs text-ink-muted leading-relaxed">
            No buddy yet. Pairs are created every Monday at 8am.
          </p>
          <Link
            to="/accountability"
            className="text-xs text-brand-teal hover:text-brand-teal-hover underline underline-offset-2 font-medium"
          >
            Learn more
          </Link>
        </div>
      )}
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-[200px] flex items-center justify-center">
      <p className="text-sm text-ink-muted text-center max-w-xs">{message}</p>
    </div>
  )
}

function BadgeIcon({ icon, label, active, color, tooltip }: { 
  icon: React.ReactNode; 
  label: string; 
  active: boolean; 
  color: string;
  tooltip: string;
}) {
  return (
    <div className="group relative">
      <div 
        className={clsx(
          "w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 border",
          active 
            ? `${color} bg-warm-card border-brand-teal/30 shadow-sm scale-110` 
            : "text-ink-faint bg-warm-inset border-warm-border opacity-40"
        )}
        title={active ? `${label} Badge: ${tooltip}` : `Locked: ${tooltip}`}
      >
        {icon}
      </div>
      <div className="absolute -bottom-1 -right-1">
        {active && <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-warm-card shadow-sm" />}
      </div>
    </div>
  )
}

function getTimeOfDay(): string {
  const h = new Date().getHours()
  if (h < 12) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}

function formatFlag(flag: string): string {
  return flag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}
